import React, { Fragment } from "react";
import { CurrencyChip } from "@/components/index.tsx";
import { Currency, CurrencyRequest } from "@/types/Currency.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, ApiResponse } from "@/types/Api.tsx";
import { FormError, ValidationErrors } from "@/types/ValidationError.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import CustomLogger from "@/utils/CustomLogger.tsx";
import { AxiosError, isAxiosError } from "axios";
import { formatError } from "@/utils/formatError.tsx";
import { CurrencyQueryKey } from "@/config/constants/QueryKeys.tsx";
import { isBitramp } from "@/utils/utils";

interface Props {
  currencies: Currency[] | null;
}

const SettingsCurrency: React.FC<Props> = ({ currencies }) => {
  const queryClient = useQueryClient();

  const [error, setError] = React.useState<{
    type: "manual" | "auto";
    message: string;
  } | null>(null);

  const [currency, setCurrency] = React.useState<CurrencyRequest>({
    symbol: "",
  });

  const addCurrencyMutation = useMutation<
    ApiResponse<Currency>,
    ApiError<ValidationErrors>,
    CurrencyRequest[]
  >({
    mutationFn: (data) =>
      GenericApi.post<Currency>(ApiRoutes.CURRENCY, [...data, currency]),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      setCurrency({
        symbol: "",
      });
      await queryClient.invalidateQueries({
        queryKey: [CurrencyQueryKey],
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
    },
  });

  const deleteCurrencyMutation = useMutation<
    ApiResponse<Currency>,
    ApiError<ValidationErrors>,
    CurrencyRequest[]
  >({
    mutationFn: (data) => GenericApi.post<Currency>(ApiRoutes.CURRENCY, data),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      setCurrency({
        symbol: "",
      });
      await queryClient.invalidateQueries({
        queryKey: [CurrencyQueryKey],
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
    },
  });

  const handleAxiosError = (error: AxiosError<unknown> | null | Error) => {
    if (isAxiosError<FormError>(error)) {
      setError({
        type: "manual",
        message: error.response?.data?.detail ?? "An error occurred",
      });
      return;
    }

    if (isAxiosError<ValidationErrors>(error)) {
      setError({
        type: "manual",
        message: formatError(error, "An error occurred"),
      });
      return;
    }

    //* Set error message from server
    setError({
      type: "manual",
      message: error?.message ?? "An error occurred",
    });
  };

  const handleAddCurrency = () => {
    setError(null);

    if (currency.symbol.trim().length === 0) {
      setError({
        type: "manual",
        message: "Currency is required",
      });
      return;
    }

    addCurrencyMutation.mutate(
      currencies?.map((currency) => {
        return {
          symbol: currency.symbol ?? "",
        };
      }) ?? []
    );
  };

  const handleDeleteCurrency = (symbol: string) => {
    setError(null);

    const filterCurrencies = currencies
      ?.filter((currency) => currency?.symbol !== symbol)
      .filter((currency) => currency?.symbol?.trim().length !== 0);

    deleteCurrencyMutation.mutate(
      filterCurrencies?.map((currency) => {
        return {
          symbol: currency.symbol ?? "",
        };
      }) ?? []
    );
  };

  const handleInputCurrency = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrency({
      symbol: String(e.target.value).toUpperCase(),
    });
  };

  return (
    <Fragment>
      <div className="w-full max-w-[1379px] mx-auto h-[280px] space-y-4">
        <div className={`${isBitramp() ? "medium pl-[0.5rem]" : "semi-bold"} w-full Text dark-blue `}>
          Exchange Available Coins
        </div>

        <div
          className={`w-full Container p-4 bg-[#efefef] ${
            isBitramp() ? "!bg-[#232323]" : ""
          }`}
        >
          <div className="flex justify-start items-center gap-3">
            <input
              onChange={handleInputCurrency}
              type="text"
              value={currency.symbol}
              placeholder="Symbol"
              className={`w-[5rem] h-[2.2rem] bg-white border-[1px] border-solid border-[#e0e0e0] rounded-[0.625rem] Input ${
                isBitramp() ? "!bg-[#212121] !text-[#fff]" : ""
              }`}
            />
            <button
              onClick={handleAddCurrency}
            //   className={`Primary_button ${
            //     isBitramp() ? "!bg-[#8AFEFD] !text-[#212121]" : ""
            //   }`}
              className={`${isBitramp() ? "Upload_button_bitramp !text-[#262626]" : "Primary_button"}`}
            >
              Add
            </button>

            {error && (
              <div className="text-red-500 text-[14px] font-medium">
                {error.message}
              </div>
            )}
          </div>

          <div
            className={`border my-4 w-full ${
              isBitramp() ? "!border-[#464646]" : ""
            }`}
          ></div>

          <div className="flex justify-start items-center flex-wrap gap-x-[1rem] ">
            {currencies && currencies?.length > 0 ? (
              currencies?.map((currency, index) => (
                <CurrencyChip
                  onDelete={handleDeleteCurrency}
                  key={index}
                  currency={currency}
                />
              ))
            ) : (
              <div className="Text dark-blue bold">No currency available</div>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default SettingsCurrency;
